import { useEffect, useState, useMemo } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Page,
  Header,
  Content,
  Table,
  TableColumn,
  Link,
  Progress,
  ResponseErrorPanel,
  HeaderLabel,
} from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import {
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  makeStyles,
  FormControlLabel,
  Switch,
} from '@material-ui/core';

function isAwsResource(entity: Entity): boolean {
  return !!entity.metadata.annotations?.['aws.amazon.com/account-id'];
}

const REQUIRED_TAGS: { key: string; label: string }[] = [
  { key: 'geniussports.com/billing-team-name', label: 'BillingTeamName' },
  { key: 'geniussports.com/billing-application', label: 'BillingApplication' },
  { key: 'geniussports.com/billing-environment', label: 'BillingEnvironment' },
];

const useStyles = makeStyles(theme => ({
  filterBar: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  select: {
    minWidth: 200,
  },
  typeChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  tagChip: {
    margin: 2,
    fontSize: '0.7rem',
  },
  missingTag: {
    margin: 2,
    fontSize: '0.7rem',
    backgroundColor: '#ffcdd2',
    color: '#b71c1c',
  },
  presentTag: {
    margin: 2,
    fontSize: '0.7rem',
    backgroundColor: '#c8e6c9',
    color: '#1b5e20',
  },
}));

const TYPE_COLORS: Record<string, string> = {
  'rds-instance': '#1565c0',
  'rds-cluster': '#6a1b9a',
  'acm-certificate': '#2e7d32',
};

interface ResourceRow {
  name: string;
  title: string;
  type: string;
  owner: string;
  namespace: string;
  account: string;
  region: string;
  tags: Record<string, string>;
  missingTags: string[];
}

export const UntaggedAwsResourcesPage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [selectedMissingTag, setSelectedMissingTag] = useState<string>('');
  const [onlyUntagged, setOnlyUntagged] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);

    catalogApi
      .getEntities({
        filter: { kind: 'Resource' },
      })
      .then(({ items: allResources }) => {
        const items = allResources.filter(isAwsResource);
        if (!active) return;

        const rows: ResourceRow[] = items.map((e: Entity) => {
          const spec = (e as any).spec ?? {};
          const annotations = e.metadata.annotations ?? {};
          const isValid = (v: string | undefined): v is string =>
            !!v && v.toLowerCase() !== 'unknown';

          const tags: Record<string, string> = {};
          for (const tag of REQUIRED_TAGS) {
            const val = annotations[tag.key];
            if (isValid(val)) tags[tag.key] = val;
          }

          const missingTags = REQUIRED_TAGS
            .filter(t => !isValid(annotations[t.key]))
            .map(t => t.key);

          return {
            name: e.metadata.name,
            title: (e.metadata as any).title ?? e.metadata.name,
            type: spec.type ?? '',
            owner: spec.owner ?? '',
            namespace: e.metadata.namespace ?? 'default',
            account: annotations['aws.amazon.com/account-id'] ?? '',
            region: annotations['aws.amazon.com/region'] ?? '',
            tags,
            missingTags,
          };
        });

        setResources(rows);
        setError(undefined);
      })
      .catch(e => {
        if (active) setError(e);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [catalogApi]);

  const filtered = useMemo(
    () =>
      resources.filter(r => {
        if (onlyUntagged && r.missingTags.length === 0) return false;
        if (selectedType && r.type !== selectedType) return false;
        if (selectedAccount && r.account !== selectedAccount) return false;
        if (selectedMissingTag && !r.missingTags.includes(selectedMissingTag))
          return false;
        return true;
      }),
    [resources, selectedType, selectedAccount, selectedMissingTag, onlyUntagged],
  );

  const untaggedCount = useMemo(
    () => resources.filter(r => r.missingTags.length > 0).length,
    [resources],
  );

  const typeOptions = useMemo(() => {
    const set = new Set(resources.map(r => r.type));
    return Array.from(set).sort();
  }, [resources]);

  const accountOptions = useMemo(() => {
    const set = new Set(resources.map(r => r.account).filter(Boolean));
    return Array.from(set).sort();
  }, [resources]);

  if (error) {
    return (
      <Page themeId="tool">
        <Header title="AWS Resource Tags" />
        <Content>
          <ResponseErrorPanel error={error} />
        </Content>
      </Page>
    );
  }

  const columns: TableColumn<ResourceRow>[] = [
    {
      title: 'Name',
      field: 'title',
      highlight: true,
      render: row => (
        <Link to={`/catalog/${row.namespace}/component/${row.name}`}>
          {row.title}
        </Link>
      ),
    },
    {
      title: 'Type',
      field: 'type',
      render: row => (
        <Chip
          label={row.type}
          size="small"
          className={classes.typeChip}
          style={{
            backgroundColor: TYPE_COLORS[row.type] ?? '#607d8b',
            color: '#fff',
          }}
        />
      ),
    },
    {
      title: 'Owner',
      field: 'owner',
      render: row => {
        const name = row.owner.split('/').pop() ?? row.owner;
        return name || '—';
      },
    },
    {
      title: 'Account',
      field: 'account',
    },
    {
      title: 'Region',
      field: 'region',
    },
    {
      title: 'Required Tags',
      sorting: false,
      render: row => (
        <>
          {REQUIRED_TAGS.map(tag =>
            row.tags[tag.key] ? (
              <Chip
                key={tag.key}
                label={`${tag.label}: ${row.tags[tag.key]}`}
                size="small"
                className={classes.presentTag}
              />
            ) : (
              <Chip
                key={tag.key}
                label={`${tag.label}: MISSING`}
                size="small"
                className={classes.missingTag}
              />
            ),
          )}
        </>
      ),
    },
  ];

  return (
    <Page themeId="tool">
      <Header
        title="AWS Resource Tags"
        subtitle="Audit required tags across all AWS resources"
      >
        <HeaderLabel label="Total" value={String(resources.length)} />
        <HeaderLabel label="Untagged" value={String(untaggedCount)} />
      </Header>
      <Content>
        {loading ? (
          <Progress />
        ) : (
          <>
            <Box className={classes.filterBar}>
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyUntagged}
                    onChange={e => setOnlyUntagged(e.target.checked)}
                    color="primary"
                  />
                }
                label="Missing Tags Only"
              />
              <FormControl
                variant="outlined"
                size="small"
                className={classes.select}
              >
                <InputLabel>Account</InputLabel>
                <Select
                  value={selectedAccount}
                  onChange={e => setSelectedAccount(e.target.value as string)}
                  label="Account"
                >
                  <MenuItem value="">All Accounts</MenuItem>
                  {accountOptions.map(a => (
                    <MenuItem key={a} value={a}>
                      {a}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                className={classes.select}
              >
                <InputLabel>Resource Type</InputLabel>
                <Select
                  value={selectedType}
                  onChange={e => setSelectedType(e.target.value as string)}
                  label="Resource Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  {typeOptions.map(t => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                className={classes.select}
              >
                <InputLabel>Missing Tag</InputLabel>
                <Select
                  value={selectedMissingTag}
                  onChange={e =>
                    setSelectedMissingTag(e.target.value as string)
                  }
                  label="Missing Tag"
                >
                  <MenuItem value="">Any</MenuItem>
                  {REQUIRED_TAGS.map(t => (
                    <MenuItem key={t.key} value={t.key}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Table
              title={`Resources (${filtered.length})`}
              columns={columns}
              data={filtered}
              options={{
                search: true,
                paging: true,
                pageSize: 25,
                pageSizeOptions: [25, 50, 100],
                padding: 'dense',
              }}
            />
          </>
        )}
      </Content>
    </Page>
  );
};
